import { useState, type FormEvent } from "react";

const FORM_ADDR = "https://readdy.ai/api/form/db6f7mfqhq9pk5mectbg";

interface FormStatus {
  type: "idle" | "success" | "error";
  message: string;
}

export default function ContactForm() {
  const [status, setStatus] = useState<FormStatus>({ type: "idle", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    const form = event.currentTarget;
    const formData = new FormData(form);

    // Honeypot check — silently mimic success for bots
    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus({ type: "success", message: "感谢你的反馈，我们已收到你的信息。" });
      form.reset();
      return;
    }

    formData.delete("website_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setSubmitting(true);
    setStatus({ type: "idle", message: "" });

    try {
      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const code = parsed?.code;
      const isSpam = typeof serverMsg === "string" && serverMsg.includes("spam");

      if (response.ok && code === "OK") {
        setStatus({
          type: "success",
          message: "感谢你的反馈，我们已收到你的信息，会尽快处理。",
        });
        form.reset();
      } else {
        setStatus({
          type: "error",
          message:
            typeof serverMsg === "string" && serverMsg
              ? `提交失败：${serverMsg}`
              : "提交失败，请稍后重试。",
        });
      }

      if (isSpam) {
        setStatus({
          type: "error",
          message: "提交失败：内容被识别为异常，请检查后重试。",
        });
      }
    } catch {
      setStatus({ type: "error", message: "网络异常，提交失败，请稍后重试。" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      id="db6f7mfqhq9pk5mectbg"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h3 className="font-heading text-lg font-bold text-foreground-950">
        意见反馈 / 联系合作
      </h3>
      <p className="mt-1 text-sm text-foreground-500">
        如对内容、版权或合作有任何疑问，欢迎留言，我们会尽快回复。
      </p>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="text-sm font-medium text-foreground-700">
            你的称呼
          </label>
          <input
            id="contact-name"
            name="name"
            required
            className="mt-1.5 w-full rounded-md border border-background-300 bg-background-100 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            placeholder="请输入称呼"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="text-sm font-medium text-foreground-700">
            邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            className="mt-1.5 w-full rounded-md border border-background-300 bg-background-100 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            placeholder="you@example.com"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-subject" className="text-sm font-medium text-foreground-700">
          反馈类型
        </label>
        <select
          id="contact-subject"
          name="subject"
          className="mt-1.5 w-full rounded-md border border-background-300 bg-background-100 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
          defaultValue="内容建议"
        >
          <option value="内容建议">内容建议</option>
          <option value="版权问题">版权问题</option>
          <option value="播放反馈">播放反馈</option>
          <option value="商务合作">商务合作</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-message" className="text-sm font-medium text-foreground-700">
          详细内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          placeholder="请描述你的问题或建议（500 字以内）"
        />
      </div>

      {/* Honeypot field */}
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="field-guard-aux"
      />

      {status.type !== "idle" && (
        <div
          className={`mt-4 rounded-md px-4 py-2.5 text-sm ${
            status.type === "success"
              ? "bg-secondary-100 text-secondary-800"
              : "bg-accent-100 text-accent-800"
          }`}
        >
          {status.message}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="mt-4 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {submitting ? "提交中…" : "提交反馈"}
      </button>
    </form>
  );
}